import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ui } from "../config/theme";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

import { 
    ArrowLeft, 
    MapPin, 
    DollarSign, 
    Tag, 
    FileText, 
    Loader2, 
    Building2, 
    User, 
    Mail, 
    Phone, 
    Calendar,
    Clock,
    Users,
    Briefcase,
    Home,
    Award,
    Globe
} from "lucide-react";

const JobDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [applying, setApplying] = useState(false);
    const { user } = useAuth();
    const [hasApplied, setHasApplied] = useState(false);

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/job-postings/${id}`);
                if (!response.ok) throw new Error('Job not found');
                const data = await response.json();
                setJob(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchJobDetails();
    }, [id]);

    // Check if the current user already applied for this job
    useEffect(() => {
        const checkAlreadyApplied = async () => {
            if (!user) {
                setHasApplied(false);
                return;
            }
            try {
                const resp = await fetch(`${API_BASE_URL}/api/applications/candidate/${encodeURIComponent(user.email)}`);
                if (!resp.ok) return;
                const apps = await resp.json();
                const applied = Array.isArray(apps) && apps.some(a => String(a.job_id) === String(id));
                setHasApplied(!!applied);
            } catch (_) {
                // ignore
            }
        };
        checkAlreadyApplied();
    }, [user, id]);

    const formatSalary = () => {
        if (!job.include_salary) return 'Salary not disclosed';
        
        if (job.minimum_salary && job.maximum_salary) {
            return `${job.currency} ${job.minimum_salary.toLocaleString()} - ${job.maximum_salary.toLocaleString()} per ${job.paid_every}`;
        } else if (job.salary) {
            return `${job.currency} ${job.salary.toLocaleString()}`;
        }
        return 'Salary not specified';
    };

    // Helper to format ISO date strings to readable format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d)) return dateString;
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className="w-full flex items-center justify-center py-16">
                            <div className="flex items-center gap-2 text-slate-600">
                                <Loader2 className="w-6 h-6 animate-spin" />
                                <span>Loading job details...</span>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className="w-full text-center py-16">
                            <p className="text-slate-600 mb-4">Error loading job details.</p>
                            <button
                                onClick={() => navigate('/job-search')}
                                className={ui.buttonLink}
                            >
                                Return to Job Search
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className="w-full mb-8">
                        <button
                            onClick={() => navigate('/job-search')}
                            className={ui.buttonLink}
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back to Job Search
                        </button>
                    </div>

                    <div className={`${ui.sectionCard} p-8`}>
                        {/* Job Header */}
                        <div className="border-b border-gray-200 pb-6 mb-6">
                            <h1 className="text-3xl font-semibold text-gray-900 mb-4">{job.title}</h1>
                            <div className="flex flex-wrap gap-4 mb-4">
                                {job.company_name && (
                                    <div className="flex items-center text-gray-600">
                                        <Building2 className="w-5 h-5 mr-2" />
                                        {job.company_name}
                                    </div>
                                )}
                                <div className="flex items-center text-gray-600">
                                    <MapPin className="w-5 h-5 mr-2" />
                                    {job.location}
                                    {job.is_remote && (
                                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                            Remote
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <DollarSign className="w-5 h-5 mr-2" />
                                    {formatSalary()}
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Tag className="w-5 h-5 mr-2" />
                                    <span className={`px-3 py-1 rounded-full text-sm ${
                                        job.status === 'Active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {job.status}
                                    </span>
                                </div>
                            </div>

                            {/* Additional Job Info */}
                            <div className="flex flex-wrap gap-4 mb-4">
                                {job.employment_type && (
                                    <div className="flex items-center text-gray-600">
                                        <Briefcase className="w-5 h-5 mr-2" />
                                        {job.employment_type}
                                    </div>
                                )}
                                {job.department && (
                                    <div className="flex items-center text-gray-600">
                                        <Building2 className="w-5 h-5 mr-2" />
                                        {job.department}
                                    </div>
                                )}
                                {job.classification && (
                                    <div className="flex items-center text-gray-600">
                                        <Award className="w-5 h-5 mr-2" />
                                        {job.classification}
                                    </div>
                                )}
                            </div>

                            {/* Date Information */}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                {job.post_date && (
                                    <div className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-1" />
                                        Posted: {formatDate(job.post_date)}
                                    </div>
                                )}
                                {job.closing_date && (
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 mr-1" />
                                        Closing: {formatDate(job.closing_date)}
                                    </div>
                                )}
                                {job.updated_at && (
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 mr-1" />
                                        Updated: {formatDate(job.updated_at)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Job Description */}
                        <div className="mb-8">
                            <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
                                <FileText className="w-5 h-5" />
                                Job Description
                            </div>
                            <p className="text-gray-600 whitespace-pre-wrap">{job.description}</p>
                        </div>

                        {/* Responsibilities */}
                        {job.responsibilities && (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
                                    <Users className="w-5 h-5" />
                                    Responsibilities
                                </div>
                                <p className="text-gray-600 whitespace-pre-wrap">{job.responsibilities}</p>
                            </div>
                        )}

                        {/* Skills */}
                        {job.skills && (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 text-gray-900 font-medium mb-4">
                                    <Award className="w-5 h-5" />
                                    Required Skills
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {job.skills.split(',').map((skill, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                        >
                                            {skill.trim()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Job Details Grid */}
                        <div className="mb-8 bg-gray-50 rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Job Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm text-gray-500">Employment Type</span>
                                    <p className="font-medium">{job.employment_type || 'Not specified'}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Department</span>
                                    <p className="font-medium">{job.department || 'Not specified'}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Classification</span>
                                    <p className="font-medium">{job.classification || 'Not specified'}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Work Location</span>
                                    <p className="font-medium">
                                        {job.is_remote ? 'Remote' : 'On-site'}
                                    </p>
                                </div>
                                {job.include_salary && (
                                    <>
                                        <div>
                                            <span className="text-sm text-gray-500">Currency</span>
                                            <p className="font-medium">{job.currency}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">Pay Frequency</span>
                                            <p className="font-medium">Per {job.paid_every}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Recruiter Information */}
                        {(job.recruiter_name || job.recruiter_email || job.recruiter_phone || job.contact_address) && (
                            <div className="mb-8 bg-gray-50 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                                <div className="space-y-3">
                                    {job.contact_address && (
                                        <div className="flex items-center text-gray-600">
                                            <Home className="w-5 h-5 mr-3" />
                                            <span>{job.contact_address}</span>
                                        </div>
                                    )}
                                    {job.recruiter_name && (
                                        <div className="flex items-center text-gray-600">
                                            <User className="w-5 h-5 mr-3" />
                                            <div>
                                                <span className="font-medium">{job.recruiter_name}</span>
                                                {job.recruiter_position && (
                                                    <span className="text-sm text-gray-500 ml-2">
                                                        - {job.recruiter_position}
                                                    </span>
                                                )}
                                                {job.recruiter_department && (
                                                    <span className="text-sm text-gray-500">
                                                        , {job.recruiter_department}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {job.recruiter_email && (
                                        <div className="flex items-center text-gray-600">
                                            <Mail className="w-5 h-5 mr-3" />
                                            <a 
                                                href={`mailto:${job.recruiter_email}`}
                                                className="text-blue-600 hover:text-blue-700 transition-colors"
                                            >
                                                {job.recruiter_email}
                                            </a>
                                        </div>
                                    )}
                                    {job.recruiter_phone && (
                                        <div className="flex items-center text-gray-600">
                                            <Phone className="w-5 h-5 mr-3" />
                                            <a 
                                                href={`tel:${job.recruiter_phone}`}
                                                className="text-blue-600 hover:text-blue-700 transition-colors"
                                            >
                                                {job.recruiter_phone}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Apply Button - Only show for non-recruiters */}
                        {user?.role !== 'recruiter' && (
                            hasApplied ? (
                                <div className="w-full sm:w-auto px-4 py-2 rounded-md bg-gray-100 text-gray-600 text-center font-medium cursor-not-allowed">
                                    You already applied for this job
                                </div>
                            ) : (
                                <button
                                    className={"w-full sm:w-auto " + ui.buttonPrimary}
                                    onClick={async () => {
                                        if (!user) {
                                            navigate('/login', { state: { from: `/job/${id}` } });
                                            return;
                                        }
                                        
                                        setApplying(true);
                                        try {
                                            const response = await fetch(`${API_BASE_URL}/api/applications`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                    job_id: id,
                                                    applicant_id: user.id,
                                                    cover_letter: "" // Add empty cover letter if needed
                                                })
                                            });
                                            
                                            const data = await response.json();
                                            
                                            if (!response.ok) {
                                                if (response.status === 409) {
                                                    setHasApplied(true);
                                                }
                                                throw new Error(data.message || 'Application failed');
                                            }
                                            
                                            setHasApplied(true);
                                            alert('Application submitted successfully!');
                                        } catch (err) {
                                            console.error('Application error:', err);
                                            alert(`Application failed: ${err.message}`);
                                        } finally {
                                            setApplying(false);
                                        }
                                    }}
                                    disabled={applying}
                                >
                                    {applying ? 'Applying...' : 'Apply for this position'}
                                </button>
                            )
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default JobDetails;